import React, { useState } from 'react';
import { Button } from 'antd';

import { checkIsCompanyOwner } from 'utils/userRolesUtils';
import BasePageCard from 'components/wrappers/BasePageCard';
import BaseAlert from 'components/wrappers/BaseAlert';
import TimesheetApproverCard from './TimesheetApproverCard';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { ProfileCorporateInfoModel } from 'api/backofficeApiClient';
import BaseSectionMessage from 'components/wrappers/BaseSectionMessage';
import EditTimesheetApproverInfoModal from './EditTimesheetApproverInfoModal';
import { userSelector } from 'redux/selectors/authenticationSelectors';
import { getTimesheetApproverInformation } from 'redux/reducers/accountSlice';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from './TimesheetApproverInfo.module.less';

type Props = {
  corporateInfo?: ProfileCorporateInfoModel;
};

export default function TimesheetApproverInfo(props: Props) {
  const { corporateInfo } = props;
  const user = useAppSelector(userSelector);
  const isCompanyOwner = checkIsCompanyOwner(user);
  const dispatch = useAppDispatch();
  const [timesheetApproverInfo, setTimesheetApproverInfo] = useState(
    corporateInfo?.timesheetApprovers,
  );
  const [isModalVisible, setIsModalVisible] = useState(false);

  const loadTimesheetApproverInfo = async () => {
    const result = await dispatch(getTimesheetApproverInformation());

    if (getTimesheetApproverInformation.fulfilled.match(result)) {
      setTimesheetApproverInfo(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      {isModalVisible && (
        <EditTimesheetApproverInfoModal
          onClose={handleCloseModal}
          onEdit={loadTimesheetApproverInfo}
          timesheetApproverInfo={timesheetApproverInfo}
        />
      )}

      <BasePageCard
        title="Timesheet approver information"
        className="stretch-card"
        icons={<span className="icon icon-timesheet-clock"></span>}
        subTitle={
          isCompanyOwner &&
          !!timesheetApproverInfo?.length && (
            <Button
              onClick={handleOpenModal}
              className="with-icon mobile-icon-only"
              size="large"
              icon={<span className="icon icon-edit" />}
              data-id="openTimesheetApproverInfoModal"
            >
              Edit
            </Button>
          )
        }
      >
        <div className="scroll-box">
          {!timesheetApproverInfo?.length && isCompanyOwner && (
            <div className="content-wrapper">
              <BaseSectionMessage
                type="info"
                title="Info"
                message={
                  <div className={styles.message}>
                    <span>
                      Please add Timesheet approver in order to select them in a
                      new assignment.
                    </span>
                    <Button
                      className={styles.button}
                      onClick={handleOpenModal}
                      type="link"
                    >
                      Fill in now.
                    </Button>
                  </div>
                }
              />
            </div>
          )}

          {!!timesheetApproverInfo?.length && (
            <>
              {timesheetApproverInfo.map((timesheetApprover, index) => (
                <TimesheetApproverCard
                  key={index}
                  timesheetApprover={timesheetApprover}
                />
              ))}
            </>
          )}
        </div>
      </BasePageCard>
    </>
  );
}
